import React, { useEffect, useRef, useState } from 'react';
import { Icon } from './Shared';
import { THEME_OPTIONS, useThemeMode, type ThemeMode } from '../lib/theme';

/**
 * Appearance control for the sidebar footer. Renders entirely from
 * {@link THEME_OPTIONS} — adding or reordering themes needs no change here.
 *
 * Expanded rail: a labelled button that opens a menu of the options.
 * Collapsed rail: an icon-only button that cycles to the next option (with the
 * current label as its tooltip), since there's no room for a popover.
 */
export function ThemeToggle({ collapsed = false }: { collapsed?: boolean }): React.ReactElement {
  const [mode, setMode] = useThemeMode();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = THEME_OPTIONS.find((o) => o.id === mode) ?? THEME_OPTIONS[0];

  // Close the menu on outside click or Escape.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const pick = (id: ThemeMode): void => {
    setMode(id);
    setOpen(false);
  };

  if (collapsed) {
    const cycle = (): void => {
      const idx = THEME_OPTIONS.findIndex((o) => o.id === mode);
      pick(THEME_OPTIONS[(idx + 1) % THEME_OPTIONS.length].id);
    };
    return (
      <button
        className="afs-item afs-theme-icon"
        onClick={cycle}
        title={`Theme: ${current.label} (click to change)`}
        aria-label={`Theme: ${current.label}`}
      >
        <span className="afs-item-icon">
          <Icon d={current.icon} />
        </span>
      </button>
    );
  }

  return (
    <div className="afs-theme" ref={ref}>
      <button
        className="afs-item afs-theme-btn"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Change theme"
      >
        <span className="afs-item-icon">
          <Icon d={current.icon} />
        </span>
        <span className="afs-item-label afs-theme-label">
          Theme
          <span className="afs-theme-value">{current.label}</span>
        </span>
        <span className={`afs-ws-caret${open ? ' afs-ws-caret--open' : ''}`}>
          <Icon d="M6 9l6 6 6-6" />
        </span>
      </button>
      {open && (
        <div className="afs-theme-menu" role="menu">
          {THEME_OPTIONS.map((o) => (
            <button
              key={o.id}
              className={`afs-theme-menu-item${o.id === mode ? ' afs-theme-menu-item--active' : ''}`}
              role="menuitemradio"
              aria-checked={o.id === mode}
              onClick={() => pick(o.id)}
            >
              <span className="afs-item-icon">
                <Icon d={o.icon} />
              </span>
              <span className="afs-theme-menu-label">{o.label}</span>
              {o.id === mode && (
                <span className="afs-theme-check" aria-hidden="true">
                  <Icon d="M5 12l5 5L20 7" size={13} />
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
